/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-params */
import type { PropsWithChildren, WeakValidationMap } from 'react';
import { useMemo, useRef } from 'react';
import type { TypescriptDocumentation } from '../propTypes/types.js';
import { describe } from '../propTypes/index.js';
import { throwRequiredError, throwTypeError } from './errorTemplates.js';
import {
  isArray,
  isFunction,
  isJSXorNode,
  isObject,
  isPrimitiveType,
  isSomethingWithParenthesis,
  isString,
  isUnion,
  isUndefined,
  isNull,
  isObjectOf,
} from './typescriptGuards.js';
import { typescriptObjectParser } from './typescriptParsers.js';

type ValidatorFn = (
  schemaName: string,
  key: string,
  value: unknown,
  format: string,
  validationsMemo: Record<symbol, string>,
  nextValidationsMemo: Record<symbol, string>,
) => void;

// =============================================================================
// Atom Validators
// =============================================================================

// This functions will validate something from the data
// and optionally recursively apply `validateValueWithFormat`
// in smaller parts

const validateUndefined: ValidatorFn = (schemaName, key, value, format) => {
  if (value !== undefined || value === 'undefined') {
    throwTypeError(schemaName, key, value, format);
  }
};
const validateNull: ValidatorFn = (schemaName, key, value, format) => {
  if (value !== null || value === 'null') {
    throwTypeError(schemaName, key, value, format);
  }
};
const validatePrimitiveType: ValidatorFn = (schemaName, key, value, format) => {
  if (typeof value !== format) {
    throwTypeError(schemaName, key, value, format);
  }
};

const validateString: ValidatorFn = (schemaName, key, value, format) => {
  if (value !== format.slice(1, -1)) {
    throwTypeError(schemaName, key, value, format);
  }
};

const validateArray: ValidatorFn = (schemaName, key, value, format, validationsMemo, nextValidationsMemo) => {
  // Check that we have an array
  if (!Array.isArray(value)) {
    throwTypeError(schemaName, key, value, format);
  }

  // Check that each element inside satisfies the format
  (value as unknown[]).forEach((val, index) => {
    // this is a recursive func, we need to invoke it before it's defined.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    validateValueWithFormat(
      schemaName,
      `${key}[${index}]`,
      val,
      format.slice(0, -2),
      validationsMemo,
      nextValidationsMemo,
    );
  });
};
function isObjectType(value: unknown): value is Record<string, unknown> {
  return !(typeof value !== 'object' || Array.isArray(value));
}

const validateObjectOf: ValidatorFn = (schemaName, key, value, format, validationsMemo, nextValidationsMemo) => {
  const valuesIsObject = isObjectType(value);
  // Check that we have an object
  if (!valuesIsObject) {
    throwTypeError(schemaName, key, value, format);
    return;
  }

  const valuesFormat = format.slice('{ [key: string]: '.length, -2);

  Object.keys(value).forEach((objectKey) => {
    // this is a recursive func, we need to invoke it before it's defined.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    validateValueWithFormat(
      schemaName,
      `${key}[${objectKey}]`,
      value[objectKey],
      valuesFormat,
      validationsMemo,
      nextValidationsMemo,
    );
  });
};

const validateObject: ValidatorFn = (schemaName, key, value, format, validationsMemo, nextValidationsMemo) => {
  const valuesIsObject = isObjectType(value);
  // Check that we have an object
  if (!valuesIsObject) {
    throwTypeError(schemaName, key, value, format);
    return;
  }

  if (format === 'object') return;

  const keyValuePairs = typescriptObjectParser(format);
  // Now we have the key - value pairs
  // Each key could either be required or not
  // Just recursively check the object

  keyValuePairs.forEach(([objectKey, objectValue]) => {
    const trueKey = objectKey.slice(-1) === '?' ? objectKey.slice(0, -1) : objectKey;

    if (trueKey === objectKey && !(trueKey in value)) {
      throwRequiredError(schemaName, key);
    }

    if (trueKey in value) {
      // this is a recursive func, we need to invoke it before it's defined.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      validateValueWithFormat(
        schemaName,
        `${key}[${trueKey}]`,
        value[trueKey],
        objectValue,
        validationsMemo,
        nextValidationsMemo,
      );
    }
  });
};

const validateUnion: ValidatorFn = (schemaName, key, value, format, validationsMemo, nextValidationsMemo) => {
  const possibilities = format.split(/\s?\|\s?/);

  const errors = [];

  possibilities.forEach((possibility) => {
    try {
      // this is a recursive func, we need to invoke it before it's defined.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      validateValueWithFormat(schemaName, key, value, possibility, validationsMemo, nextValidationsMemo);
    } catch (e) {
      errors.push(e);
    }
  });

  if (errors.length === possibilities.length) {
    throwTypeError(schemaName, key, value, format);
  }
};

const validateFunction: ValidatorFn = (schemaName, key, value, format) => {
  // Check that we have a function
  if (typeof value !== 'function') {
    throwTypeError(schemaName, key, value, format);
  }
};

function isJSXElement(value: unknown): value is JSX.Element {
  return value === null || (typeof value === 'object' && value !== null && '$$typeof' in value);
}
const validateJSXorNode: ValidatorFn = (schemaName, key, value, format) => {
  const valueIsJSX = isJSXElement(value);
  if (format === 'JSX.Element' && !valueIsJSX) {
    throwTypeError(schemaName, key, value, format);
  }
};

// =============================================================================
// Schema validator
// =============================================================================

const validateValueWithFormat: ValidatorFn = (schemaName, key, value, format, validationsMemo, nextValidationsMemo) => {
  nextValidationsMemo[value as symbol] = format;

  if ((value as symbol) in validationsMemo) {
    // We already validated this value on this format
    return;
  }

  if (isUndefined(format)) {
    validateUndefined(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isNull(format)) {
    validateNull(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isPrimitiveType(format)) {
    validatePrimitiveType(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isUnion(format)) {
    validateUnion(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isString(format)) {
    validateString(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isArray(format)) {
    validateArray(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isObjectOf(format)) {
    validateObjectOf(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isObject(format)) {
    validateObject(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isFunction(format)) {
    validateFunction(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isJSXorNode(format)) {
    validateJSXorNode(schemaName, key, value, format, validationsMemo, nextValidationsMemo);
  } else if (isSomethingWithParenthesis(format)) {
    validateValueWithFormat(schemaName, key, value, format.slice(1, -1), validationsMemo, nextValidationsMemo);
  }
};

export const validateTypescriptPropTypesImplementation = (
  props: PropsWithChildren<Record<string, unknown>>,
  schema: TypescriptDocumentation,
  validationsMemo: Record<string, string> = {},
  nextValidationsMemo: Record<string, string> = {},
): void => {
  const { properties, name: schemaName } = schema;

  properties.forEach((property) => {
    const { name, format, required, isRequiredIf, omitValidation } = property;

    const requiredConditionally = isRequiredIf && isRequiredIf(props);

    if (omitValidation) {
      return;
    }

    if (required && !(name in props)) {
      throwRequiredError(schema.name, name);
    }

    if (requiredConditionally && !(name in props)) {
      throwRequiredError(schema.name, name);
    }

    const shouldValidateProperty = props[name] !== undefined || required || requiredConditionally;

    if (name in props && format && shouldValidateProperty) {
      validateValueWithFormat(schemaName, name, props[name], format, validationsMemo, nextValidationsMemo);
    }
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useValidateTypescriptPropTypes = <T = Record<string, any>>(
  props: PropsWithChildren<T>,
  propTypes: WeakValidationMap<unknown>,
  componentDisplayName: string,
): void => {
  if (!Boolean(componentDisplayName)) {
    throw new Error('Provide display name for the component to use for validation');
  }

  const validationsMemo = useRef<Record<string, string>>({});

  const typescriptSchema = useMemo(() => {
    const Component = () => {};

    const DescribedComponent = describe(Component);
    DescribedComponent.propTypes = propTypes;
    DescribedComponent.displayName = componentDisplayName;
    return DescribedComponent.toTypescript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propTypes]);

  useMemo(() => {
    const nextValidationsMemo = {};

    validateTypescriptPropTypesImplementation(props, typescriptSchema, validationsMemo.current, nextValidationsMemo);
    validationsMemo.current = nextValidationsMemo;
    return 'WE DONT NEED RETURN just the memo';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);
};
